import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import moment from 'moment-timezone';
import { GDD_THRESHOLD, TRAP_CATCH_COLOUR } from '../../utils';

const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const ONE_WEEK_MS = 7 * ONE_DAY_MS;

const GenerationTimingsChart = ({
  dates,
  sprayData,
  trapCatchData,
  gddAccumulationData
}) => {
  const trapCatchSeries = trapCatchData
    .map((d) => [moment.tz(d.date, 'Pacific/Auckland').valueOf(), d.amount])
    .sort((a, b) => a[0] - b[0]);

  const thresholdIndex = gddAccumulationData.findIndex(
    (v) => Number(v) >= GDD_THRESHOLD
  );
  const cutoff = thresholdIndex === -1 ? dates.length : thresholdIndex + 1;
  const gddAccumulationSeries = dates
    .slice(0, cutoff)
    .map((seconds, i) => [seconds * 1000, Number(gddAccumulationData[i])]);

  const sortedSprays = (sprayData || [])
    .filter((sd) => sd.date)
    .sort(
      (a, b) =>
        moment.tz(a.date, 'Pacific/Auckland').valueOf() -
        moment.tz(b.date, 'Pacific/Auckland').valueOf()
    );

  const sprayBands = sortedSprays.map((spray, i) => {
    const sprayDate = moment.tz(spray.date, 'Pacific/Auckland').valueOf();
    const from = sprayDate - ONE_WEEK_MS;
    const to = from + (spray.efficacy_days || 0) * ONE_DAY_MS;
    return { from, to, number: i + 1, spray };
  });

  const sprayPlotBands = sprayBands.map(({ from, to, number }) => ({
    from,
    to,
    color: 'rgba(144, 238, 144, 0.15)',
    label: {
      text: `#${number}`,
      style: {
        color: '#666',
        fontSize: '12px',
        fontWeight: 'bold'
      },
      align: 'center',
      verticalAlign: 'top',
      y: 20
    }
  }));

  const options = {
    chart: {
      type: 'line'
    },
    credits: {
      enabled: false
    },
    time: {
      timezone: 'Pacific/Auckland'
    },
    title: {
      text: 'Generation Timings & Spray Dates'
    },
    tooltip: {
      shared: true,
      formatter: function () {
        const x = this.x;
        let html = this.points.reduce(
          (s, point) => {
            const dp = point.series.options.custom?.decimals;
            const value =
              dp != null ? Highcharts.numberFormat(point.y, dp) : point.y;
            return (
              s +
              `<br/><span style="color:${point.color}">\u25CF</span> ${point.series.name}: <b>${value}</b>`
            );
          },
          `<span style="font-size: 10px">${Highcharts.dateFormat('%A, %b %e, %Y', x)}</span>`
        );

        const activeSprays = sprayBands.filter(
          (band) => x >= band.from && x <= band.to
        );
        if (activeSprays.length) {
          activeSprays.forEach((band) => {
            html += `<br/><span style="color:green">\u25CF</span> Spray #${band.number} (${band.spray.efficacy_days} days)`;
          });
        }

        return html;
      }
    },
    xAxis: {
      type: 'datetime',
      tickInterval: 2 * 7 * 24 * 3600 * 1000,
      gridLineWidth: 1,
      labels: {
        format: '{value:%e-%b}'
      },
      plotBands: sprayPlotBands
    },
    yAxis: [
      {
        title: {
          text: 'G2 Trap Catch'
        }
      },
      {
        title: {
          text: "G3 GDD's"
        },
        opposite: true
      }
    ],
    legend: {
      enabled: true
    },
    plotOptions: {
      line: {
        marker: {
          enabled: false
        }
      }
    },
    series: [
      {
        name: 'G2 Trap Catch',
        data: trapCatchSeries,
        color: TRAP_CATCH_COLOUR,
        yAxis: 0
      },
      {
        name: "G3 GDD's",
        data: gddAccumulationSeries,
        color: 'black',
        custom: {
          decimals: 2
        },
        yAxis: 1
      }
    ]
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

export default GenerationTimingsChart;

